import * as React from "react";
import {cx}       from 'emotion';
import {
    faSearch,
    faSpinner,
    faTimesCircle,
}                            from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon}     from "@fortawesome/react-fontawesome";

import "./user-management-layout-mobile.scss";

import {cs}               from "@Common/react/chain-services";
import {consumeContext}   from "@Common/react/context";
import {Load}             from "@Common/react/load";
import {UseState}         from "@Common/react/use-state";
import {Types}            from "@Common/types";
import {Responsive}       from "@Common/ui-components/responsive";
import {SearchableSelect} from "@Common/ui-components/searchable-select/searchable-select";
import {SliderCheckbox}   from "@Common/ui-components/slider-checkbox/slider-checkbox";
import {sortMulti}        from "@Common/utils/collections";
import {chain}            from "@Common/utils/fs";
import {parseUrlQuery}    from "@Common/utils/parse-url-query";

import {COMPANY_LEVEL}       from "@Xpresso/routes/common/logic/user-level";
import {UseObserver}         from "./use-observer";
import {UserCreateModal}     from "./user-create-modal/user-create-modal";
import {UserCardMobile}      from "./user-management-card/user-management-card-mobile";
import {UserManagementModal} from "./user-management-modal/user-management-modal";

const dummyObserver = {observe: () => null, disconnect: () => null};
export const UserManagementLayoutMobile = (props:Types.Xpresso.Account.Admin.UserManagement.UserManagementLayoutProps) => cs(
    consumeContext("apis"),
    consumeContext("userLevels"),
    consumeContext("routerHistory"),
    ["branchList", ({apis}, next) => Load({
        fetch: () => apis.next.getBranchList(),
        next,
    })],
    ["query", ({}, next) => next(parseUrlQuery(window.location.search))],
    ["idBranch", ({query}, next) => UseState({next, initValue: query?.office ?? ""})],
    ["intersection", (...[, next]:any[]) => (
        <UseObserver
            makeObserver={(callback) => "IntersectionObserver" in window ? new IntersectionObserver(callback) : dummyObserver}
            onEntries={makeIntersectionObserverEntriesHandler(props.onLoadMoreRequest)}
            next={next}
        />
    )],
    ["columns", (...[, next]:any[]) => UseState({next, initValue: null})],
    ["resize", ({columns}, next) => (
        <UseObserver
            makeObserver={(callback) => "ResizeObserver" in window ? new ResizeObserver(callback) : dummyObserver}
            onEntries={makeResizeObserverEntriesHandler(props.onColumnCountChange, columns)}
            next={next}
        />
    )],
    ["searchState", (...[, next]:any[]) => UseState({next, initValue: ""})],
    ({query}, next) => UserManagementModal({
        initShow: query && query['agent'],
        reload: () => props.reload(),
        next,
        baseUrl: '/account/admin/user',
    }),
    ({query}, next) => UserCreateModal({
        initShow: query && query['modal'] && query['modal'] === "create",
        initValue: {...(query && query['idBranch'] && {idBranch: query['idBranch']})},
        reload: () => props.reload(),
        next,
    }),
    ["responsive", (_, next) => <Responsive {...{
        sizes: {
            md: 600,
        },
        next,
    }}/>],
    ({intersection, resize, searchState, idBranch, branchList, userLevels, routerHistory, responsive}:{
        intersection:Types.Xpresso.Account.Admin.UserManagement.UseObserverValue,
        resize:Types.Xpresso.Account.Admin.UserManagement.UseObserverValue,
        searchState:Types.Common.React.UseState.UseStateNext<string>
        idBranch:Types.Common.React.UseState.UseStateNext<string>
        branchList:Types.Common.React.Load2.Load2Next<Types.Apis.Payloads.Next.Branch.GetAll.Response>,
        userLevels:any,
        routerHistory:any,
        responsive:any,
    }) => (
        <UserManagementContent
            gridRef={resize.targetRef as React.RefObject<HTMLDivElement>}
            searchState={searchState}
            idBranch={idBranch}
            spinnerRef={intersection.targetRef as React.RefObject<HTMLDivElement>}
            branchList={branchList}
            userLevels={userLevels}
            routerHistory={routerHistory}
            responsive={responsive}
            {...props}
        />
    ),
);

const UserManagementContent = ({
    canLoadMore,
    gridRef,
    isLoading,
    searchIsActive,
    searchState,
    idBranch,
    showingInactive,
    spinnerRef,
    users,
    branchList,
    userLevels,
    routerHistory,
    responsive,
    onBranchChangeRequest       : handleBranchChangeRequest,
    onUserCreateRequest         : handleUserCreateRequest,
    onLoadMoreRequest           : handleLoadMoreRequest,
    onSearchChangeRequest       : handleSearchChangeRequest,
    onShowInactiveSwitchRequest : handleShowInactiveSwitchRequest,
}:Types.Xpresso.Account.Admin.UserManagement.UserManagementContentProps) => (
    <div className="user-management-layout-mobile-ci3m">
        <div className={cx("filters-ui", {stacking: responsive.lt("md")})}>
            <label className="slider-label"><span className="text" xd-qa-type="label" xd-qa-name="Show Inactive">Show Inactive</span><SliderCheckbox
                disabled={isLoading}
                onChange={handleShowInactiveSwitchRequest}
                value={showingInactive}
                xdQaName={"Show Inactive Slider " + showingInactive}
            /></label>
            <div className={`input-with-button${searchIsActive ? " active" : ""}`}>
                <input
                    type="text"
                    disabled={isLoading}
                    value={searchState.value}

                    onChange={(e) => searchState.onChange(e.currentTarget.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearchChangeRequest(searchState.value)}

                    placeholder="Search for a user"

                    xd-qa-type="textbox"
                    xd-qa-name="Search User"
                />
                <button
                    onClick={(e) => {
                        if (searchIsActive) {
                            searchState.onChange("");
                            handleSearchChangeRequest("");
                            (e.currentTarget.previousElementSibling as HTMLInputElement).focus();
                        } else
                            handleSearchChangeRequest(searchState.value)
                    }}
                    disabled={isLoading}
                    tabIndex={-1}
                    xd-qa-type="button"
                    xd-qa-name="Search"
                ><FontAwesomeIcon icon={searchIsActive ? faTimesCircle : faSearch} xd-qa-type="icon" xd-qa-name="Magnifying Glass"/></button>
            </div>
            <div className="branch-dropdown">
                {userLevels.userLevel === COMPANY_LEVEL && SearchableSelect({
                    getSublabel: (branch) => (<>
                        <div
                            title={[
                                branch.branchAddress1,
                                branch.branchAddress2,
                                branch.branchCity,
                                branch.branchState,
                                branch.branchZip,
                            ].filter(Boolean).join(", ")}
                        >
                            {[
                                branch.branchAddress1,
                                branch.branchAddress2,
                                branch.branchCity,
                                branch.branchState,
                                branch.branchZip,
                            ].filter(Boolean).join(", ")}
                        </div>
                        <div
                            title={`${branch.branchLinkId} ${userLevels.internalUser && "| Internal ID: "} ${branch.idBranch}`}
                            xd-qa-type="dropdown_item"
                            xd-qa-name={`Office ${branch.idBranch}`}
                        >
                            {branch.branchLinkId} {userLevels.internalUser && `| Internal ID: ${branch.idBranch}`}
                        </div>
                    </>),
                    list: chain(
                        branchList ?? [],
                        (_) => sortMulti(_, [
                            (branch) => branch["branchName"]?.toLowerCase(),
                        ]),
                    ),
                    loading: !branchList,
                    placeholder: "Select an office",
                    getLabel: (branch) => chain(
                        branch.branchName || branch.branchLinkId,
                        (label) => <span title={label} xd-qa-type="dropdown_item" xd-qa-name={`Office ${label}`}>{label}</span>
                    ),
                    mapSearchAsItemFilter: (search:string) => {
                        const searchLowercase = search.toLowerCase();
                        return (branch) => (branch.idBranch.includes(searchLowercase)) || [
                            "branchName",
                            "branchLinkId",
                            "branchAddress1",
                            "branchAddress2",
                            "branchCity",
                            "branchState",
                        ].some(field => branch[field].toLowerCase().includes(searchLowercase))
                    },
                    onSelect: async (branch) => {
                        handleBranchChangeRequest(branch?.idBranch);
                        idBranch.onChange(branch?.idBranch);
                    },
                    isSelected: (branch) => idBranch.value === branch.idBranch,
                    allowClear: true,
                    className: "office",
                    searchLabel: "Search an office",
                    xdQaName: "Office",
                    scrollCloses: true,
                })}
            </div>
        </div>
        <hr />
        <div ref={gridRef} className="users-grid">
            {((users.length > 0 || !isLoading) && handleUserCreateRequest) && <div className="item new-user-item">
                <button aria-label="Create New User" xd-qa-type="button" xd-qa-name="Create New User" onClick={handleUserCreateRequest}>Create new user</button>
            </div>}
            {users.map((user, uniqueKey) => (
                UserCardMobile({user: user, userLevel: userLevels.userLevel, uniqueKey: uniqueKey, routerHistory})
            ))}
        </div>

        {canLoadMore && <div ref={spinnerRef} className="spinner-footer">{isLoading ? (
            <FontAwesomeIcon icon={faSpinner} className="fa-pulse"/>
        ) : (
            <button onClick={handleLoadMoreRequest}>Load more</button>
        )}</div>}
    </div>
);

const makeIntersectionObserverEntriesHandler = (handleLoadMoreRequest:() => void) => {
    return (entries:IntersectionObserverEntry[]) => {
        const intersectionOcurred = entries.find(entry => entry.isIntersecting);
        if (intersectionOcurred)
            handleLoadMoreRequest();
    }
};

const makeResizeObserverEntriesHandler = (handleColumnCountChange:(colCount:number) => void, columns: Types.Common.React.UseState.UseStateNext<number>) => {
    let lastColCount = columns.value;
    return (entries:ResizeObserverEntry[]) =>
    {
        const
            lastEntry = entries.pop(),
            grid      = lastEntry.target as HTMLDivElement,
            colCount  = window.getComputedStyle(grid).gridTemplateColumns.split(" ").length
        ;

        if (lastColCount !== colCount) {
            columns.onChange(colCount);
            handleColumnCountChange(colCount);
        }
    };
};
